import React from 'react';
import { useSubscription } from '../../contexts/subscription-context';
import { LLM_FAMILIES, LLM_MODELS, MODEL_TO_FAMILY_MAP, getLLMConfig } from '../../shared/models';

export default function UsageStats() {
  const { modelUsage, lifetimeUsage, subscription, isModelAvailable, remainingChats, hasActiveSubscription } = useSubscription();
  const llmConfig = getLLMConfig();

  // Group models by family, but only include models the user has access to
  const groupedModels = Object.entries(llmConfig).reduce((acc, [modelId, config]) => {
    // Only include models the user has access to
    if (isModelAvailable(modelId)) {
      if (!acc[config.family]) {
        acc[config.family] = [];
      }
      acc[config.family].push([modelId, config]);
    }
    return acc;
  }, {});

  // Helper to get remaining uses for pro tier models or free tier GPT-4o-mini
  const getRemainingUses = (modelId) => {
    // For free users using GPT-4o-mini, show remaining chats
    if (modelId === LLM_MODELS.GPT_4O_MINI && !hasActiveSubscription()) {
      return remainingChats;
    }
    
    // For pro tier models with limits
    if (!llmConfig[modelId]?.limitedUsage) return null;
    const monthlyLimit = llmConfig[modelId]?.monthlyLimit || 0;
    const currentUsage = modelUsage[modelId] || 0;
    return monthlyLimit - currentUsage;
  };

  // Only show families that have at least one accessible model
  const familiesWithModels = Object.keys(groupedModels).filter(
    family => groupedModels[family].length > 0
  );

  if (familiesWithModels.length === 0) {
    return (
      <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800 p-6 mb-6">
        <h2 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text mb-4">
          Model Usage Statistics
        </h2>
        <p className="text-gray-400 text-sm">
          No model usage statistics available for your current plan.
          {!subscription?.status === 'active' && (
            <span> Upgrade to access more advanced AI models.</span>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-800 p-6 mb-6">
      <h2 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text mb-4">
        Model Usage Statistics
      </h2>

      <div className="space-y-6">
        {familiesWithModels.map((family) => (
          <div key={family} className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-300 border-b border-gray-800 pb-2">
              {family === LLM_FAMILIES.GPT ? 'GPT Models' : 
               family === LLM_FAMILIES.CLAUDE ? 'Claude Models' : 
               family === LLM_FAMILIES.GEMINI ? 'Gemini Models' : 'Other Models'}
            </h3>
            
            <div className="space-y-2">
              {groupedModels[family].map(([modelId, config]) => {
                const monthlyUses = modelUsage[modelId] || 0;
                const lifetimeUses = lifetimeUsage[modelId] || 0;
                const remaining = getRemainingUses(modelId);

                return (
                  <div key={modelId} className="flex items-center justify-between py-2">
                    <div className="flex-1">
                      <div className="text-sm text-gray-300 flex items-center gap-2">
                        {config.name}
                        {config.requiresUltimate && (
                          <span className="text-xs font-medium bg-gradient-to-r from-blue-400 via-violet-400 to-indigo-400 text-transparent bg-clip-text border border-indigo-400/50 rounded px-1">
                            Ultimate
                          </span>
                        )}
                        {config.requiresPro && !config.requiresUltimate && (
                          <span className="text-xs font-medium text-purple-400 border border-purple-400/50 rounded px-1">
                            Pro
                          </span>
                        )}
                      </div>
                      
                      <div className="text-xs text-gray-500 mt-1">
                        {remaining !== null && (
                          <span className={subscription?.tier === 'pro' ? 'text-purple-400' : 'text-gray-400'}>
                            {remaining} {subscription?.tier === 'pro' ? 'uses' : 'debates'} remaining this month
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="text-right">
                      <div className="text-sm text-gray-300">
                        {monthlyUses} <span className="text-gray-500">this month</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {lifetimeUses} lifetime
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}